import type { ItemsPerPage, SortDirection } from '@wyz/types';
import { ITEMS_PER_PAGE } from '@wyz/types';
import {
  parseFormData,
  serializeFormData,
} from '../../utils/helpers/query-string';
import { isEqual, isNil, omit } from 'lodash-es';
import { IFilterForm } from './types';

/**
 * Query string keys owned by the datatable itself. Any other key found in the
 * search params is considered to be a filter form value.
 */
export const RESERVED_KEYS = ['page', 'limit', 'sortField', 'sortDirection'];

type Sort = { sortField: string; sortDirection: SortDirection } | undefined;

export type DatatableUrlState = {
  page: number;
  limit: ItemsPerPage;
  sort: Sort;
  formData: IFilterForm;
};

const isItemsPerPage = (value: unknown): value is ItemsPerPage =>
  ITEMS_PER_PAGE.includes(value as ItemsPerPage);

const isSortDirection = (value: unknown): value is SortDirection =>
  value === 'ASC' || value === 'DESC';

/** A filter the user left blank, whichever shape the field uses for "blank". */
const isBlank = (value: unknown) =>
  isNil(value) || value === '' || isEqual(value, []);

/**
 * `sortField=''` is how an explicitly removed sort is encoded, so that it can be
 * told apart from "the url says nothing, use the default sort".
 */
const readSort = (raw: IFilterForm, fallback: Sort): Sort => {
  if (raw.sortField === '') {
    return undefined;
  }
  if (
    typeof raw.sortField !== 'string' ||
    !isSortDirection(raw.sortDirection)
  ) {
    return fallback;
  }
  return { sortField: raw.sortField, sortDirection: raw.sortDirection };
};

/**
 * Filter values are layered on top of `defaults.formData` rather than replacing
 * it, so a query string that says nothing about a filter leaves the developer's
 * default in place — including when the query string only holds keys the
 * datatable knows nothing about.
 *
 * An empty value is how an explicitly cleared filter is encoded (see
 * `serializeDatatableState`); it is normalized to `undefined` so that it beats
 * the default and gets dropped by `formToCondition`.
 */
const mergeFormData = (
  defaults: IFilterForm,
  overlay: IFilterForm,
): IFilterForm =>
  Object.entries(overlay).reduce(
    (acc, [key, value]) => {
      acc[key] = value === '' ? undefined : value;
      return acc;
    },
    { ...defaults },
  );

/**
 * Read a datatable state out of a query string. Anything the query string does
 * not carry falls back to `defaults`.
 */
export const parseDatatableSearch = (
  search: string,
  defaults: DatatableUrlState,
): DatatableUrlState => {
  const raw = parseFormData<IFilterForm>(search);
  const page = Number(raw.page);
  const limit = Number(raw.limit);

  return {
    page: Number.isInteger(page) && page > 0 ? page : defaults.page,
    limit: isItemsPerPage(limit) ? limit : defaults.limit,
    sort: readSort(raw, defaults.sort),
    formData: mergeFormData(defaults.formData, omit(raw, RESERVED_KEYS)),
  };
};

/**
 * Write a datatable state into a query string, omitting everything that is
 * already the default so an untouched list keeps a clean url.
 */
export const serializeDatatableState = (
  state: DatatableUrlState,
  defaults: DatatableUrlState,
): string => {
  // Filter values are a diff against the defaults: a value that still equals
  // its default stays out of the url (so an untouched list keeps a clean one),
  // and a value the user emptied is written as an explicit `key=` so it can be
  // told apart from a value the url simply does not mention. Keys the defaults
  // know nothing about — custom filter fields, params belonging to the page
  // rather than to the datatable — always differ, and are therefore preserved.
  const formData = Object.entries(state.formData).reduce(
    (acc, [key, value]) => {
      const fallback = defaults.formData[key];
      if (isEqual(value, fallback)) {
        return acc;
      }
      if (isBlank(value)) {
        // Only worth writing when there is a non-blank default to override,
        // otherwise a blank field would needlessly show up in the url.
        if (!isBlank(fallback)) {
          acc[key] = '';
        }
        return acc;
      }
      acc[key] = value;
      return acc;
    },
    {} as IFilterForm,
  );
  const isDefaultSort = isEqual(state.sort, defaults.sort);

  return serializeFormData({
    ...formData,
    page: state.page === defaults.page ? undefined : state.page,
    limit: state.limit === defaults.limit ? undefined : state.limit,
    sortField: isDefaultSort ? undefined : (state.sort?.sortField ?? ''),
    sortDirection: isDefaultSort ? undefined : state.sort?.sortDirection,
  });
};
